import { WebStorageStateStore } from "oidc-client-ts";
import type { AuthProviderProps } from "react-oidc-context";
import { config } from "./config";

const origin = window.location.origin;

// Authorization-code + PKCE against the Drawboard Projects identity server,
// reusing the existing `web_client`. We request all three portal scopes —
// `projects-admin` is the baseline that gates portal access at all (refused
// if the user isn't in drawboard-admin); `projects-devs` and `cloud-team`
// are additive, silently dropped by the identity server for users who don't
// hold the corresponding role. The resulting access token's scope claim
// drives which surfaces the SPA renders.
export const oidcConfig: AuthProviderProps = {
  authority: config.authority,
  client_id: config.clientId,
  redirect_uri: `${origin}/callback`,
  post_logout_redirect_uri: `${origin}/`,
  response_type: "code",
  scope: `openid profile email offline_access ${config.adminScope} projects-devs cloud-team`,
  // Interactive sign-in and sign-out start at the identity server's outage routes, which set the
  // `db_outage_bypass` cookie and 302 to /connect/authorize and /connect/endsession with the same
  // query. While an outage placeholder covers the identity server's pages, only a request carrying
  // that cookie reaches the real pages. `metadataSeed` is merged OVER the discovery document in
  // oidc-client-ts >= 3.5 (MetadataService.getMetadata: Object.assign({}, discovered, seed)), so these
  // two endpoints win and every other endpoint (token, userinfo, jwks, issuer) still comes from discovery.
  metadataSeed: {
    authorization_endpoint: `${config.authority}/outage/admin-signin`,
    end_session_endpoint: `${config.authority}/outage/admin-signout`,
  },
  // We requested offline_access and `web_client` allows it, so renewal uses
  // the refresh-token grant at /connect/token — no hidden iframe / silent-renew page needed. There is
  // deliberately no `silent_redirect_uri`: a user without a refresh token fails the renew instead of
  // falling back to a prompt=none iframe against /connect/authorize, which a placeholder would answer.
  automaticSilentRenew: true,
  // The checksession iframe (/connect/checksession) is a page route; never open it.
  monitorSession: false,
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Clean ?code=&state= out of the URL after the callback completes.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, "/");
  },
};

/** Did the identity server reject the sign-in because the user isn't an admin? */
export function isAccessDenied(error: Error | undefined): boolean {
  if (!error) return false;
  const msg = `${error.name} ${error.message}`.toLowerCase();
  return msg.includes("access_denied") || msg.includes("access denied") || msg.includes("forbidden");
}
